import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Statistic,
  Tag,
  Typography,
  DatePicker,
  Button,
  Spin,
  message,
  Select,
  Space,
  Divider,
  Descriptions,
  Empty
} from 'antd';
import {
  FileTextOutlined,
  DollarOutlined,
  ReloadOutlined,
  DownloadOutlined,
  PrinterOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import vendorBillService, { type VendorStatementData, type VendorStatementTransaction } from '@/services/vendorBillService';
import apiService from '@/services/apiService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface Vendor {
  id: string;
  name: string;
  code: string;
  email?: string;
  phone?: string;
}

const VendorStatement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [data, setData] = useState<VendorStatementData | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('year'),
    dayjs()
  ]);

  // Fetch vendors list
  const fetchVendors = async () => {
    setVendorLoading(true);
    try {
      const response: any = await apiService.get('/accounting/vendors');
      setVendors(response.data || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch vendors');
    } finally {
      setVendorLoading(false);
    }
  };

  // Fetch statement data
  const fetchStatement = async () => {
    if (!selectedVendorId) return;

    setLoading(true);
    try {
      const result = await vendorBillService.getVendorStatement(
        selectedVendorId,
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      setData(result);
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch vendor statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (selectedVendorId) {
      fetchStatement();
    }
  }, [selectedVendorId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const transactionColumns: ColumnsType<VendorStatementTransaction> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (value) => dayjs(value).format('YYYY-MM-DD')
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'BILL' ? 'blue' : 'green'}>
          {type}
        </Tag>
      )
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (value) => <Text strong>{value}</Text>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      align: 'right',
      width: 130,
      render: (value) => value > 0 ? (
        <Text style={{ color: '#ff4d4f' }}>
          <ArrowUpOutlined /> {formatCurrency(value)}
        </Text>
      ) : '-'
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      align: 'right',
      width: 130,
      render: (value) => value > 0 ? (
        <Text style={{ color: '#52c41a' }}>
          <ArrowDownOutlined /> {formatCurrency(value)}
        </Text>
      ) : '-'
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      align: 'right',
      width: 130,
      render: (value) => <Text strong>{formatCurrency(value)}</Text>
    }
  ];

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            Vendor Statement
          </Title>
          <Text type="secondary">
            View detailed transaction history and balances for vendors
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<PrinterOutlined />} disabled={!data}>Print</Button>
            <Button icon={<DownloadOutlined />} disabled={!data}>Export PDF</Button>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} md={8}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Select Vendor</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder="Select a vendor"
              loading={vendorLoading}
              showSearch
              optionFilterProp="children"
              value={selectedVendorId}
              onChange={setSelectedVendorId}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={vendors.map(v => ({
                value: v.id,
                label: `${v.code} - ${v.name}`
              }))}
            />
          </Col>
          <Col xs={24} md={10}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>Date Range</Text>
            </div>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
            />
          </Col>
          <Col xs={24} md={6}>
            <div style={{ marginBottom: 8, opacity: 0 }}>
              <Text>Actions</Text>
            </div>
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={fetchStatement}
              disabled={!selectedVendorId}
              loading={loading}
            >
              Generate Statement
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Statement Content */}
      <Spin spinning={loading}>
        {data ? (
          <>
            {/* Vendor Info */}
            <Card title="Vendor Information" style={{ marginBottom: 24 }}>
              <Descriptions column={{ xs: 1, sm: 2, md: 3 }}>
                <Descriptions.Item label="Vendor Name">
                  <Text strong>{data.vendor.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Vendor Code">
                  <Tag>{data.vendor.code}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {data.vendor.email || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {data.vendor.phone || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Address" span={2}>
                  {data.vendor.address || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Summary Cards */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Opening Balance"
                    value={data.openingBalance}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Total Bills"
                    value={data.summary.totalBills}
                    prefix={<ArrowUpOutlined />}
                    precision={2}
                    valueStyle={{ color: '#ff4d4f', fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Total Payments"
                    value={data.summary.totalPayments}
                    prefix={<ArrowDownOutlined />}
                    precision={2}
                    valueStyle={{ color: '#52c41a', fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="Closing Balance"
                    value={data.closingBalance}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{
                      color: data.closingBalance > 0 ? '#ff4d4f' : '#52c41a',
                      fontSize: 18
                    }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Transactions Table */}
            <Card
              title={
                <Space>
                  <span>Transaction Details</span>
                  <Tag>
                    {dayjs(data.period.fromDate).format('MMM D, YYYY')} - {dayjs(data.period.toDate).format('MMM D, YYYY')}
                  </Tag>
                </Space>
              }
            >
              <Table
                dataSource={data.transactions}
                columns={transactionColumns}
                rowKey={(record, index) => `${record.reference}-${index}`}
                pagination={{ defaultPageSize: 15, showSizeChanger: true }}
                size="small"
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                      <Table.Summary.Cell index={0} colSpan={4}>
                        <Text strong>Period Summary</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong style={{ color: '#ff4d4f' }}>
                          {formatCurrency(data.summary.totalBills)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Text strong style={{ color: '#52c41a' }}>
                          {formatCurrency(data.summary.totalPayments)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <Text strong style={{ color: data.closingBalance > 0 ? '#ff4d4f' : '#52c41a' }}>
                          {formatCurrency(data.closingBalance)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={6}>
                        <Text strong>Balance Due</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong style={{ fontSize: 16, color: data.summary.totalDue > 0 ? '#ff4d4f' : '#52c41a' }}>
                          {formatCurrency(data.summary.totalDue)}
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />

              {data.transactions.length === 0 && (
                <Empty description="No transactions in selected period" />
              )}
            </Card>
          </>
        ) : (
          <Card>
            <div style={{ textAlign: 'center', padding: 60 }}>
              <UserOutlined style={{ fontSize: 48, color: '#ccc' }} />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">
                  Select a vendor and date range to generate statement
                </Text>
              </div>
            </div>
          </Card>
        )}
      </Spin>
    </div>
  );
};

export default VendorStatement;